import type { FC, ReactElement } from 'react';
import { useMemo } from 'react';

import { L10nText } from '../../../../../services/uiTranslations/reactBindings/L10nText';
import type { Message } from '../../../../../store/slices/chat/types';
import { JoinDelimiter, MessageType } from '../../../../../store/slices/chat/types';
import { useModifiers } from '../../../../../styles/hooks/useModifiers';
import { getIsSupportMessage, parseSystemMessage } from '../../../../../utils/chat';
import { Icon } from '../../../../icons/icon';
import { Icons } from '../../../../icons/iconNames';
import { MessageContentConnected } from '../messageContent/MessageContentConnected';

import styles from './MessageBubble.module.css';

interface ExtraPropsType {
  currentUserId: string;
  currentUserName?: string;
  isMiniChat?: boolean;
}

interface MessageProps
  extends Pick<
    Message,
    'content' | 'userId' | 'messageId' | 'userScreenName' | 'requireTranslation' | 'messageType' | 'messageParams'
  > {}

interface MessageBubbleProps extends MessageProps, ExtraPropsType {}

export const MessageBubble: FC<MessageBubbleProps> = ({
  content,
  userId,
  messageId,
  userScreenName,
  currentUserId,
  currentUserName,
  requireTranslation,
  isMiniChat,
  messageType = MessageType.GameUser,
  messageParams,
}) => {
  const isPrivateHostMessage = messageType === MessageType.Host;
  const isCurrentUserMessage = userId === currentUserId;
  const isSupportMessage = getIsSupportMessage(messageType);
  const isNotificationMsg = messageType === MessageType.Notification;

  const mods = useModifiers(
    styles,
    'messageBubble',
    {
      accent: isSupportMessage,
      border: isCurrentUserMessage,
      dark: isCurrentUserMessage || isSupportMessage || isNotificationMsg,
      left: !isCurrentUserMessage,
      miniChat: isMiniChat,
      right: isCurrentUserMessage,
      privateChat: isPrivateHostMessage,
    },
    { withBaseClass: true }
  );

  const notificationMods = useModifiers(styles, 'messageBubble', {
    notification: isNotificationMsg,
  });

  // Render username label based on message type
  const renderUserName = useMemo((): ReactElement | null => {
    if (isCurrentUserMessage) {
      return (
        <>
          <L10nText tk="CHAT_YOU" />:{' '}
        </>
      );
    }

    if (isSupportMessage) {
      return (
        <>
          <Icon className={styles.messageBubble__liveSupportIcon} icon={Icons.Support} />
          <L10nText tk="LIVE_SUPPORT" />:{' '}
        </>
      );
    }

    if (isPrivateHostMessage) {
      return (
        <>
          <Icon className={styles.messageBubble__privateChatIcon} icon={Icons.PriveChat} />
          <L10nText tk="GAME_HOST" />:{' '}
        </>
      );
    }

    return userScreenName ? <>{userScreenName}: </> : null;
  }, [isCurrentUserMessage, isPrivateHostMessage, isSupportMessage, userScreenName]);

  const renderMessageContent = useMemo((): ReactElement => {
    if (messageType === MessageType.System) {
      if (isMiniChat) {
        return <span>{parseSystemMessage(content)}</span>; // Return one-liner without HTML tags
      }

      return <div dangerouslySetInnerHTML={{ __html: content }} />; // Preserve HTML tags and layout
    }

    const dynamicValues =
      messageType === MessageType.Host && messageId === 'dealer_welcome_message'
        ? { username: currentUserName || '' }
        : messageParams;
    const joinWith = messageType === MessageType.AutomatedSupport ? JoinDelimiter.NewLine : JoinDelimiter.Space;

    return (
      <span className={notificationMods}>
        <MessageContentConnected
          content={content}
          requireTranslation={requireTranslation}
          dynamicValues={dynamicValues}
          joinWith={joinWith}
        />
      </span>
    );
  }, [
    messageType,
    content,
    messageId,
    currentUserName,
    messageParams,
    notificationMods,
    requireTranslation,
    isMiniChat,
  ]);

  return (
    <div className={mods}>
      <div className={styles.messageBubble__content}>
        <span className={styles.messageBubble__userName}>{renderUserName}</span>
        {renderMessageContent}
      </div>
    </div>
  );
};
